import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, CarIcon, SmartphoneIcon, FileTextIcon, DownloadIcon, ChevronDownIcon, AlertCircleIcon, BookOpenIcon, ArrowRightIcon, TrendingUpIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface PolicyHistoryProps {
  navigate: (screen: string) => void;
}
export function PolicyHistory({
  navigate
}: PolicyHistoryProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [expandedId, setExpandedId] = useState<number | null>(1);
  const [activeCategory, setActiveCategory] = useState('All');
  const tabs = [{
    id: 'all',
    label: 'All'
  }, {
    id: 'active',
    label: 'Active'
  }, {
    id: 'pending',
    label: 'Pending'
  }];
  const policies = [{
    id: 1,
    product: 'Future Scholar Fund',
    icon: ShieldCheckIcon,
    status: 'active',
    underwriter: 'Leadway Assurance Plc',
    policyNo: 'LWY/EDU/2026/001',
    premium: 16666,
    frequency: 'Monthly',
    nextPayment: '2026-04-15',
    sumAssured: 10000000,
    savingsProgress: 42,
    hasFinalCert: true
  }, {
    id: 2,
    product: 'AutoGuard (Comp)',
    icon: CarIcon,
    status: 'active',
    underwriter: 'AXA Mansard',
    policyNo: 'AXA/MOT/2026/842',
    premium: 150000,
    frequency: 'Annually',
    nextPayment: '2027-01-10',
    sumAssured: 5000000,
    savingsProgress: null,
    hasFinalCert: true
  }, {
    id: 3,
    product: 'TravelGuard',
    icon: SmartphoneIcon,
    status: 'pending',
    underwriter: 'Cornerstone Insurance',
    policyNo: 'Pending Registration',
    premium: 500,
    frequency: 'Monthly',
    nextPayment: '2026-04-12',
    sumAssured: 450000,
    savingsProgress: null,
    hasFinalCert: false
  }, {
    id: 4,
    product: 'GoalGuard',
    icon: TrendingUpIcon,
    status: 'active',
    underwriter: 'Leadway Assurance Plc',
    policyNo: 'LWY/GGD/2026/001',
    premium: 50000,
    frequency: 'Monthly',
    nextPayment: '2026-04-15',
    sumAssured: 5000000,
    savingsProgress: 60,
    hasFinalCert: true
  }];
  const filteredPolicies = activeTab === 'all' ? policies : policies.filter(p => p.status === activeTab);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-2337">
      <TopBar title="My Plans" onNotificationClick={() => navigate('notifications')} onProfileClick={() => navigate('profile')} data-id="element-2338" />

      <div className="px-5 pt-4 pb-6" data-id="element-2339">
        {/* Header & Stats */}
        <div className="mb-8" data-id="element-2340">
          <div data-id="element-2341">
            <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-2342">Plans</h1>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2343">
              Manage your active plans and certificates.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2344">
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2345">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2346">
                Active
              </p>
              <p className="text-2xl font-bold text-[#0D3B3B]" data-id="element-2347">3</p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2348">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2349">
                Total Value
              </p>
              <p className="text-xl font-bold text-emerald-600" data-id="element-2350">₦14.5M</p>
            </div>
          </div>
        </div>

        {/* Featured Banner */}
        <div className="bg-[#FFD700] rounded-[2rem] p-6 text-[#0D3B3B] mb-8 shadow-xl shadow-[#0D3B3B]/10 border border-[#FFD700]/50" data-id="element-2351">
          <div className="grid grid-cols-2 gap-4" data-id="element-2352">
            <div className="text-lg font-black text-[#0D3B3B] tracking-tight mb-4 px-1" data-id="element-2353">
              {activeCategory === 'All' ? 'All Products' : activeCategory}
            </div>
            <div className="text-right" data-id="element-2354">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2355">
                Active
              </p>
              <p className="text-2xl font-bold text-[#0D3B3B]" data-id="element-2356">3</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar pb-1" data-id="element-2357">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-[#0D3B3B] text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`} data-id="element-2358">
              {tab.label}
            </button>)}
        </div>

        {/* Policies List */}
        <div className="space-y-4" data-id="element-2359">
          <AnimatePresence mode="popLayout" data-id="element-2360">
            {filteredPolicies.map(policy => <motion.div key={policy.id} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className={`bg-white rounded-3xl border overflow-hidden transition-shadow flex flex-col ${expandedId === policy.id ? 'border-amber-200 shadow-lg shadow-amber-500/10' : 'border-gray-100 shadow-sm hover:shadow-md'}`} data-id="element-2361">
                {/* Card Header (Always visible) */}
                <div onClick={() => setExpandedId(expandedId === policy.id ? null : policy.id)} className="p-5 cursor-pointer flex items-center justify-between bg-white relative z-10" data-id="element-2362">
                  <div className="flex items-center gap-4" data-id="element-2363">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${policy.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-2364">
                      <policy.icon className="w-6 h-6" data-id="element-2365" />
                    </div>
                    <div data-id="element-2366">
                      <h3 className="font-bold text-[#0D3B3B] text-base mb-0.5" data-id="element-2367">
                        {policy.product}
                      </h3>
                      <div className="flex items-center gap-2 text-xs font-medium" data-id="element-2368">
                        <span className={policy.status === 'active' ? 'text-emerald-600' : 'text-amber-600'} data-id="element-2369">
                          {policy.status === 'active' ? 'Active' : 'Pending'}
                        </span>
                        <span className="text-gray-300" data-id="element-2370">•</span>
                        <span className="text-gray-500" data-id="element-2371">
                          {policy.underwriter}
                        </span>
                      </div>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: expandedId === policy.id ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400" data-id="element-2372">
                    <ChevronDownIcon className="w-5 h-5" data-id="element-2373" />
                  </motion.div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence data-id="element-2374">
                  {expandedId === policy.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="border-t border-gray-50 bg-gray-50/50" data-id="element-2375">
                      <div className="p-5 space-y-5" data-id="element-2376">
                        {/* Key Details Grid */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-2377">
                          <div data-id="element-2378">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2379">
                              Plan Number
                            </p>
                            <p className="text-sm font-mono font-bold text-[#0D3B3B]" data-id="element-2380">
                              {policy.policyNo}
                            </p>
                          </div>
                          <div data-id="element-2381">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2382">
                              Guaranteed Amount
                            </p>
                            <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2383">
                              {formatNaira(policy.sumAssured)}
                            </p>
                          </div>
                          <div data-id="element-2384">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2385">
                              Contribution
                            </p>
                            <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2386">
                              {formatNaira(policy.premium)}
                              <span className="text-xs text-gray-500 font-medium" data-id="element-2387">
                                /{policy.frequency.toLowerCase()}
                              </span>
                            </p>
                          </div>
                          <div data-id="element-2388">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2389">
                              Next Payment
                            </p>
                            <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2390">
                              {formatDate(policy.nextPayment)}
                            </p>
                          </div>
                        </div>

                        {/* Savings Progress (if applicable) */}
                        {policy.savingsProgress && <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2391">
                            <div className="flex justify-between items-end mb-2" data-id="element-2392">
                              <div data-id="element-2393">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2394">
                                  Savings Goal
                                </p>
                                <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2395">
                                  {policy.savingsProgress}% Reached
                                </p>
                              </div>
                              <p className="text-xs font-bold text-emerald-600" data-id="element-2396">
                                On track
                              </p>
                            </div>
                            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-2397">
                              <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${policy.savingsProgress}%`
                      }} transition={{
                        duration: 1,
                        delay: 0.2
                      }} className="h-full bg-emerald-500 rounded-full" data-id="element-2398" />
                            </div>
                          </div>}

                        {/* Documents Section */}
                        <div data-id="element-2399">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2400">
                            Documents
                          </p>
                          <div className="space-y-2" data-id="element-2401">
                            {policy.hasFinalCert ? <button className="w-full flex items-center justify-between p-3 bg-white border border-emerald-100 rounded-xl hover:border-emerald-200 transition-colors group" data-id="element-2402">
                                <div className="flex items-center gap-3" data-id="element-2403">
                                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-2404">
                                    <FileTextIcon className="w-4 h-4" data-id="element-2405" />
                                  </div>
                                  <div className="text-left" data-id="element-2406">
                                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2407">
                                      Final Certificate
                                    </p>
                                    <p className="text-[10px] text-gray-500" data-id="element-2408">
                                      PDF • 2.4 MB
                                    </p>
                                  </div>
                                </div>
                                <DownloadIcon className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" data-id="element-2409" />
                              </button> : <div className="w-full flex items-center justify-between p-3 bg-white border border-amber-100 rounded-xl" data-id="element-2410">
                                <div className="flex items-center gap-3" data-id="element-2411">
                                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-2412">
                                    <AlertCircleIcon className="w-4 h-4" data-id="element-2413" />
                                  </div>
                                  <div className="text-left" data-id="element-2414">
                                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2415">
                                      Temporary Cover Note
                                    </p>
                                    <p className="text-[10px] text-amber-600 font-medium" data-id="element-2416">
                                      Final cert pending underwriter approval
                                    </p>
                                  </div>
                                </div>
                                <button className="p-2 text-gray-400 hover:text-amber-600 transition-colors" data-id="element-2417">
                                  <DownloadIcon className="w-5 h-5" data-id="element-2418" />
                                </button>
                              </div>}

                            <button className="w-full flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-colors group" data-id="element-2419">
                              <div className="flex items-center gap-3" data-id="element-2420">
                                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-2421">
                                  <FileTextIcon className="w-4 h-4" data-id="element-2422" />
                                </div>
                                <div className="text-left" data-id="element-2423">
                                  <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2424">
                                    Plan Document
                                  </p>
                                  <p className="text-[10px] text-gray-500" data-id="element-2425">
                                    Terms & Conditions
                                  </p>
                                </div>
                              </div>
                              <DownloadIcon className="w-5 h-5 text-gray-400 group-hover:text-[#0D3B3B] transition-colors" data-id="element-2426" />
                            </button>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2" data-id="element-2427">
                          <button onClick={() => navigate('goalguard-claim')} className="flex-1 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0D3B3B] shadow-sm hover:bg-gray-50 transition-colors" data-id="element-2428">
                            Make a Claim
                          </button>
                          <button className="flex-1 py-3 bg-[#0D3B3B] rounded-xl text-sm font-bold text-white shadow-md hover:bg-[#092D2D] transition-colors" data-id="element-2429">
                            Pay Contribution
                          </button>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}